import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiErrorDetail } from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShieldCheck, KeyRound, Check, X } from 'lucide-react';
import { toast } from 'sonner';

// Same policy the backend enforces (validate_password_strength).
const RULES = [
  { key: 'length', label: 'At least 12 characters', test: (p) => p.length >= 12 },
  { key: 'upper', label: 'One uppercase letter (A-Z)', test: (p) => /[A-Z]/.test(p) },
  { key: 'lower', label: 'One lowercase letter (a-z)', test: (p) => /[a-z]/.test(p) },
  { key: 'digit', label: 'One digit (0-9)', test: (p) => /\d/.test(p) },
  {
    key: 'special',
    label: 'One special character (!@#$%^&*...)',
    test: (p) => /[!@#$%^&*()_+\-=\[\]{};':",.<>/?\\|`~]/.test(p),
  },
];

const AccountSecurity = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const ruleState = useMemo(
    () => RULES.map((r) => ({ ...r, passed: r.test(next) })),
    [next]
  );
  const allRulesPassed = ruleState.every((r) => r.passed);
  const passwordsMatch = next.length > 0 && next === confirm;
  const canSubmit =
    current.length > 0 &&
    next.length > 0 &&
    confirm.length > 0 &&
    allRulesPassed &&
    passwordsMatch &&
    !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (next === current) {
      toast.error('New password must be different from the current password');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      toast.success('Password changed. Please log in again.');
      // Clear local auth state and redirect
      await logout();
      navigate('/admin/login', { replace: true });
    } catch (error) {
      toast.error(
        formatApiErrorDetail(error.response?.data?.detail) ||
          'Failed to change password'
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-8" data-testid="account-security-section">
      <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-[#D4AF37]" />
        Account Security
      </h2>
      <Card className="bg-[#121212] border border-[#D4AF37]/20 p-6">
        <div className="mb-4">
          <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[#D4AF37]" />
            Change Admin Password
          </h3>
          <p className="text-sm text-[#A0A0A0]">
            For security, changing your password will end all active sessions and require you to log in again.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 max-w-lg" autoComplete="off">
          <div>
            <Label htmlFor="cp-current" className="text-white text-sm">
              Current password
            </Label>
            <Input
              id="cp-current"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="mt-2 bg-[#080808] border-[#D4AF37]/20 text-white"
              autoComplete="current-password"
              data-testid="input-current-password"
              required
            />
          </div>

          <div>
            <Label htmlFor="cp-new" className="text-white text-sm">
              New password
            </Label>
            <Input
              id="cp-new"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="mt-2 bg-[#080808] border-[#D4AF37]/20 text-white"
              autoComplete="new-password"
              data-testid="input-new-password"
              required
            />
          </div>

          <div>
            <Label htmlFor="cp-confirm" className="text-white text-sm">
              Confirm new password
            </Label>
            <Input
              id="cp-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-2 bg-[#080808] border-[#D4AF37]/20 text-white"
              autoComplete="new-password"
              data-testid="input-confirm-password"
              required
            />
            {confirm.length > 0 && !passwordsMatch && (
              <p className="text-xs text-red-400 mt-1" data-testid="mismatch-warning">
                Passwords do not match
              </p>
            )}
          </div>

          <ul className="text-xs space-y-1" data-testid="password-rules">
            {ruleState.map((r) => (
              <li
                key={r.key}
                className={`flex items-center gap-2 ${
                  r.passed ? 'text-green-400' : 'text-[#A0A0A0]'
                }`}
                data-testid={`rule-${r.key}-${r.passed ? 'pass' : 'fail'}`}
              >
                {r.passed ? (
                  <Check className="w-3.5 h-3.5" />
                ) : (
                  <X className="w-3.5 h-3.5" />
                )}
                {r.label}
              </li>
            ))}
          </ul>

          <Button
            type="submit"
            disabled={!canSubmit}
            className="bg-[#D4AF37] text-[#080808] hover:bg-[#F3A6C8] hover:text-[#080808] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="btn-change-password"
          >
            {submitting ? 'Changing…' : 'Change password'}
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default AccountSecurity;
